import {Injectable} from "@angular/core";

import {ApiService} from "app/services/domain/api.service";
import * as SharedSvc from "app/services/domain/base.service";
import {BillingPlanDefinitionsService} from "app/services/domain/billing/billing-plan/billing-plan-definitions.service";
import {BillingSourceType} from "app/services/domain/billing/billing-source";
import {extractTypedRules} from "app/billing-plans/shared/billing-rule-types";
import {ServiceAccountFetcher} from "app/services/domain/service-accounts/account-governance-list.service";
import * as Models from "app/services/proxy/model/models";
import {GroupBySelector} from "app/services/ui/group-by-selector";
import {WidgetQueryBuilderAssistant} from "app/shared/filters/widget-filter/widget-filtering";
import {findQueryOperatorByType, findQueryOperators, QueryOperatorLogicExtended, QueryOperatorLogicNode} from "app/shared/query-group/query-operator-query-node";
import {formatUpdatedOn} from "app/shared/utils/billing-utils";

import {CacheService} from "framework/services/cache.service";
import {ErrorService} from "framework/services/error.service";
import {UtilsService} from "framework/services/utils.service";
import {MomentHelper} from "framework/ui/formatting/date-format.pipe";
import {inParallel} from "framework/utils/concurrency";

@Injectable()
export class BillingPlanDefinitionVersionsService extends SharedSvc.BaseDefinitionVersionService<Models.BillingPlanConfiguration, Models.BillingPlanDefinitionVersion, Models.BillingPlanDefinition, BillingPlanDefinitionVersionExtended>
{
    constructor(api: ApiService,
                errors: ErrorService,
                cache: CacheService)
    {
        super(api, errors, cache, Models.BillingPlanDefinitionVersion, BillingPlanDefinitionVersionExtended.newInstance);
    }

    private syncStatusCache = new Map<string, Date>();

    //--//

    async makeHead(id: string): Promise<BillingPlanDefinitionVersionExtended>
    {
        let model = await this.api.billingPlanDefinitionVersionsApi.makeHead(id);
        return this.wrapModel(model);
    }

    async makeRelease(id: string): Promise<BillingPlanDefinitionVersionExtended>
    {
        await this.api.billingPlanDefinitionVersionsApi.makeRelease(id);
        return this.getExtendedById(id);
    }

    createVersion(model: Models.BillingPlanDefinitionVersion): Promise<Models.BillingPlanDefinitionVersion>
    {
        return this.api.billingPlanDefinitionVersionsApi.create(model);
    }

    //--//

    protected cachePrefix(): string { return "BillingPlanDefinitionVersion"; }

    protected getRaw(id: string): Promise<Models.BillingPlanDefinitionVersion>
    {
        return this.api.billingPlanDefinitionVersionsApi.get(id);
    }

    protected getBatchRaw(ids: string[]): Promise<Models.BillingPlanDefinitionVersion[]>
    {
        const request = Models.RecordIdentityBatchRequest.newInstance({ids: ids.map(id => BillingPlanDefinitionVersionExtended.newIdentity(id))});
        return this.api.billingPlanDefinitionVersionsApi.batch(request);
    }

    protected canBatch(): boolean
    {
        return true;
    }

    private billingAggregationRequest(rule: Models.BillingRule,
                                      metric: string,
                                      filters: Models.QueryOperator,
                                      isK8Request: boolean = false): Models.BillingAggregationRequest
    {
        return Models.BillingAggregationRequest.newInstance({
                                                                filters,
                                                                start_date    : rule.start_date,
                                                                end_date      : rule.end_date,
                                                                billing_source: metric,
                                                                ...(isK8Request ? {platform: Models.Platforms.kubernetes} : {})
                                                            });
    }

    public async customCostMetricSettingsAggregationResult(scope: Models.QueryOperator): Promise<Models.BillingAggregationResult[]>
    {
        const parsedGroupByValue = UtilsService.parseEnumValue<Models.BillingGroupBySelector>(GroupBySelector, GroupBySelector.resource_id);
        const selectedGroupBy    = [Models.BillingGroupByDetails.newInstance({selector: parsedGroupByValue})];
        const date               = MomentHelper.nowWithUTC()
                                               .startOf("day");
        const request            = Models.BillingAggregationRequest.newInstance({
                                                                                    billing_source: BillingSourceType.BilledCost,
                                                                                    group_by      : selectedGroupBy,
                                                                                    filters       : scope,
                                                                                    start_date    : date.clone()
                                                                                                        .subtract(3, "year")
                                                                                                        .toDate(),
                                                                                    end_date      : date.toDate()
                                                                                });

        return await this.api.billing.getCostAggregation(request);
    }

    private sumAggregationResult(results: Models.BillingAggregationResult[]): CostCurrency[]
    {
        const groupedByCurrency = results.reduce((acc,
                                                  agg) =>
                                                 {
                                                     const currency = agg.identity.currency;
                                                     acc[currency]  = (acc[currency] || 0) + agg.value;
                                                     return acc;
                                                 }, {} as Record<Models.Currency, number>);

        return Object.entries(groupedByCurrency)
                     .map(([currency, cost]) => ({
                         cost    : Number(cost.toFixed(2)),
                         currency: currency as Models.Currency
                     }));
    }

    private async getAssociatedClusterIds(cloudAccountIds: string[]): Promise<string[]>
    {
        const query = Models.ServiceAccountListRequest.newInstance({
                                                                       filters: Models.ServiceAccountFilter.newInstance({
                                                                                                                            native_details              : [new Models.ServiceAccountNativeDetailsBaseForKubernetesCluster()],
                                                                                                                            resource_service_account_ids: cloudAccountIds,
                                                                                                                            statuses                    : [
                                                                                                                                Models.ServiceAccountStatus.active,
                                                                                                                                Models.ServiceAccountStatus.inactive
                                                                                                                            ]
                                                                                                                        })
                                                                   });

        const {results} = await this.domain.apis.serviceAccountGovernanceApi.listServiceAccounts(query);
        return results;
    }

    private async buildK8sQuery(groupingRules: Models.DimensionGroupingRule[],
                                rootRule: Models.BillingRuleRoot): Promise<Models.QueryOperator | undefined>
    {
        const hasPlatformFilters = groupingRules.some(item => item.platform_filters);
        const [
                  pricingRules,
                  costAllocationLineItemRules
              ]                  = extractTypedRules(rootRule?.rules || []);

        if (!pricingRules.length && !costAllocationLineItemRules.length && !hasPlatformFilters) return undefined;

        const targetIds: string[] = [];
        if (costAllocationLineItemRules.length)
        {
            const dimensionCostCenterDetails = await this.domain.filterOptions.resolver.getDimensionCostCenters();

            for (const rule of costAllocationLineItemRules)
            {
                if (!(rule instanceof Models.BillingRuleAllocationGenericTargets)) continue;
                for (const target of rule.targets)
                {
                    if (!(target instanceof Models.AllocationDimensionGroupingTarget)) continue;
                    const targetName   = `CostCenter_${target.dimension_name}_${target.grouping_rule_name}`;
                    const targetDetail = dimensionCostCenterDetails.find(item => item.service_account_name == targetName);
                    targetIds.push(targetDetail?.service_account_id);
                }
            }
        }

        let k8sQuery: Models.QueryOperator | undefined;

        for (const groupingRule of groupingRules)
        {
            const platformQuery       = groupingRule.platform_filters;
            const isOperatorNaryLogic = platformQuery instanceof Models.QueryOperatorNaryLogic;
            const k8sPlatformQuery    = isOperatorNaryLogic ? platformQuery.terms : [platformQuery];

            const updatedQuery = await Promise.all(k8sPlatformQuery.map(async query =>
                                                                        {
                                                                            const serviceAccountQuery = findQueryOperatorByType<Models.ServiceAccountQueryOperator>(query,
                                                                                                                                                                    Models.ServiceAccountQueryOperator);
                                                                            if (!serviceAccountQuery) return query;
                                                                            const clusterIds = await this.getAssociatedClusterIds(serviceAccountQuery?.service_account_ids);
                                                                            if (targetIds.length) clusterIds.push(...targetIds);
                                                                            const cleanedQuery = WidgetQueryBuilderAssistant.remove(query, Models.ServiceAccountQueryOperator.newInstance({}));
                                                                            const clusterQuery = Models.ServiceAccountQueryOperator.newInstance({service_account_ids: clusterIds});
                                                                            return WidgetQueryBuilderAssistant.merge(cleanedQuery, clusterQuery);
                                                                        }));

            const mergedQuery = isOperatorNaryLogic ? Models.QueryOperatorNaryLogic.newInstance({
                                                                                                    op   : Models.ResourceNaryQueryCondition.NotAny,
                                                                                                    terms: updatedQuery
                                                                                                }) : updatedQuery[0];

            k8sQuery = QueryOperatorLogicNode.orAppendQuery(k8sQuery, mergedQuery);
        }

        return k8sQuery;
    }

    public async fetchPricingData(rootRule: Models.BillingRuleRoot,
                                  rule: Models.BillingRule,
                                  fetcher?: ServiceAccountFetcher,
                                  getLastSyncDate: boolean = false): Promise<CostBeforeAfter>
    {
        const originalSource = rule instanceof Models.BillingRuleCalculate && rule.metric ? rule.metric : rootRule.source_metric;

        const source      = originalSource || BillingSourceType.BilledCost;
        const destination = rootRule.destination_metric || BillingSourceType.PartnerContractedCost;

        const scope                   = QueryOperatorLogicExtended.extractQueryOperatorTrue(QueryOperatorLogicExtended.parse(rule.scope)).toModel();
        const dimensions              = await this.domain.dimensions.getDimensions(fetcher);
        const dimensionQueryOperators = findQueryOperators<Models.DimensionQueryOperator>(scope, node => node instanceof Models.DimensionQueryOperator);
        const dimensionIds            = new Set(dimensionQueryOperators.map(op => op.dimension_id));
        const groupingRuleIds         = new Set(dimensionQueryOperators.flatMap(op => op.values));
        const groupingRules           = dimensions.filter(d => dimensionIds.has(d.id))
                                                  .flatMap(d => d.details?.grouping_rules ?? [])
                                                  .filter(gr => groupingRuleIds.has(gr.grouping_rule_id));

        const requests: Models.BillingAggregationRequest[] = [
            this.billingAggregationRequest(rule, source, scope),
            this.billingAggregationRequest(rule, destination, scope)
        ];

        const k8sQuery = await this.buildK8sQuery(groupingRules, rootRule);
        if (k8sQuery) requests.push(this.billingAggregationRequest(rule, source, k8sQuery, true), this.billingAggregationRequest(rule, destination, k8sQuery, true));

        const {results}                            = await this.api.billing.getCostAggregationBatch(Models.BillingAggregationRequestBatch.newInstance({requests}));
        const [before, after, k8sBefore, k8sAfter] = results;

        const costBeforeAfter: CostBeforeAfter = {
            before: this.sumAggregationResult(before),
            after : this.sumAggregationResult(after)
        };

        if (getLastSyncDate) costBeforeAfter.lastUpdatedDate = await this.getLastUpdatedDetails(after);

        if (k8sBefore && k8sAfter)
        {
            costBeforeAfter.k8sBefore = this.sumAggregationResult(k8sBefore);
            costBeforeAfter.k8sAfter  = this.sumAggregationResult(k8sAfter);

            if (getLastSyncDate) costBeforeAfter.k8sLastUpdatedDate = await this.getLastUpdatedDetails(k8sAfter);
        }

        return costBeforeAfter;
    }

    private async getLastUpdatedDetails(items: Models.BillingAggregationResult[]): Promise<Date | null>
    {
        let maxTime = 0;
        await inParallel(items, async (item) =>
        {
            const completedAt = await this.getCompletedAtDetails(item.identity.service_account_id);
            if (completedAt)
            {
                const time = completedAt.getTime();
                if (time > maxTime) maxTime = time;
            }
        });
        return maxTime ? new Date(maxTime) : null;
    }

    private async getCompletedAtDetails(serviceAccountId: string): Promise<Date | null>
    {
        if (this.syncStatusCache.has(serviceAccountId)) return this.syncStatusCache.get(serviceAccountId)!;

        const syncStatus = await this.domain.apis.serviceAccountGovernanceApi.getServiceAccountSyncStatus(serviceAccountId);

        const date = syncStatus?.billing_info?.completed_at || null;
        if (date) this.syncStatusCache.set(serviceAccountId, date);

        return date;
    }

    public getTooltipContent(updatedOn: string | Date): string
    {
        const {
                  formattedTime,
                  formattedDate,
                  timeAgo
              } = formatUpdatedOn(updatedOn);
        return formattedDate ? `Last updated on ${formattedDate} at ${formattedTime} (${timeAgo})` : "";
    }
}

export interface CostCurrency
{
    cost: number;
    currency: Models.Currency;
    tooltipText?: string;
}

interface CostBeforeAfter
{
    before?: CostCurrency[];
    after?: CostCurrency[];
    lastUpdatedDate?: Date | null;
    k8sBefore?: CostCurrency[];
    k8sAfter?: CostCurrency[];
    k8sLastUpdatedDate?: Date | null;
}

export interface CostProcessingDetails
{
    cloud: CostCurrency[];
    k8s: CostCurrency[];
    cloudTooltipText?: string;
    k8sTooltipText?: string;
}

export class BillingPlanDefinitionVersionExtended extends SharedSvc.ExtendedDefinitionVersionModel<Models.BillingPlanConfiguration, Models.BillingPlanDefinitionVersion, Models.BillingPlanDefinition>
{
    static newInstance(svc: BillingPlanDefinitionVersionsService,
                       model: Models.BillingPlanDefinitionVersion): BillingPlanDefinitionVersionExtended
    {
        return new BillingPlanDefinitionVersionExtended(svc, model, "BillingPlanDefinitionVersion");
    }

    public static newIdentity(sysId?: string): Models.RecordIdentity
    {
        return Models.RecordIdentity.newInstance({
                                                     table: "BillingPlanDefinitionVersion",
                                                     sysId: sysId
                                                 });
    }

    //--//

    protected definitionSvc(): BillingPlanDefinitionsService
    {
        return this.domain.billingPlanDefinitions;
    }

    protected definitionVersionSvc(): BillingPlanDefinitionVersionsService
    {
        return this.domain.billingPlanDefinitionVersions;
    }
}
